import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Button,
  Card,
  Col,
  Descriptions,
  Dropdown,
  Form,
  Menu,
  Modal,
  Row,
  Space,
  Spin,
  Switch,
  Tabs,
  Tooltip,
  Typography,
} from 'antd';
import {
  ExclamationCircleOutlined,
  DeleteOutlined,
  CloseOutlined,
  CaretDownOutlined,
  RedoOutlined,
  CopyOutlined
} from '@ant-design/icons';
import copy from 'copy-text-to-clipboard';
import dayjs from 'dayjs';

import theme from 'assets/styles/theme';
import ProductDetail from 'components/Inventory/ProductDetail/ProductDetailsDialog';
import {
  ColumnDataType,
  CopySpan,
  DataGridPlus,
  ExportCSV,
  FormLabel,
  FormPlus,
  FormSubmitButton,
  GreyCardWrapper,
  HeaderTitle,
  PrintLabel as PrintLabelDialog,
  SpaceCell,
  TextArea,
  message,
} from 'components/common';
import { DEFAULT_DATE_TIME_FORMAT, DATAGRID_CELL_ICON_WIDTH1 } from 'constants/config';
import { poControlStatus, poStatusMap } from 'constants/enums'
import {
  fetchPo,
  patchPo,
  fetchPoLineList,
  // delPurchaseOrder,
  poControlStatusClose,
  poControlStatusOpen,
  abandonPurchaseOrder,
} from 'services/purchaseOrder';
import { convertTimeByUtc, getRouterParameters } from 'utils';

type PoDetailProps = {
  isModal?: boolean;
  poInfo?: any;
}

const { Text } = Typography;

const PoDetail = (props:PoDetailProps) => {
  const [changed, setChanged] = useState<boolean>(false);
  const [current, setCurrent] = React.useState<ProductRow>({
    id: 0,
    productId: '',
  });
  const [data, setData] = useState<Array<PoLineRow>>([]);
  const [detailVisible, setDetailVisible] = React.useState(false);
  const [edited, setEdited] = useState<boolean>(false);
  const [labelData, setLabelData] = useState<PrintLabelTypes[]>([]);
  const [loading, setLoading] = useState(false);
  const [record, setRecord] = useState<PoRow | null>();
  const [printLabelVisible, setPrintLabelVisible] = useState(false);
  const [saveLoading, setSaveLoading] = useState<boolean>(false);
  const [selected, setSelected] = React.useState<any>({});
  const [selectedRows, setSelectedRows] = useState<StringKAnyVPair[]>([]);
  const [skuList, setSkuList] = useState<string[]>([]);
  const [snapshot, setSnapshot] = useState<StringKAnyVPair>({});
  const [upcList, setUpcList] = useState<string[]>([]);
  const [values, setValues] = useState<StringKAnyVPair>({});

  const [form] = Form.useForm();
  const history = useHistory();

  const detailLayout = {
    labelCol: { span: 8 },
    wrapperCol: { span: 16 },
  };
  const columns = [
    {
      header: 'SKU',
      name: 'sku',
      minWidth: 220,
      userSelect: true,
      defaultFlex: 1,
      render: (value: any) => {
        const { data, rowIndex } = value;

        return (
          <SpaceCell
            id={`catalog_grid_product_sku_row_${rowIndex}`}
            icon={
              <Tooltip
                placement="top"
                title="Copy"
                trigger={['hover', 'click']}
              >
                <CopyOutlined
                  onClick={() => {
                    copy(data.sku);
                    message.info(`"${data.sku}" has been copied to clipboard`);
                  }}
                  style={{ color: theme['@info-color'] }}
                />
              </Tooltip>
            }
            iconWidth={DATAGRID_CELL_ICON_WIDTH1}
            onTextClick={() => onClickSkuCell(data)}
            text={data.sku}
            textIsButton
          />
        );
      },
    },
    {
      header: 'Part Number',
      name: 'partNumber',
      userSelect: true,
      defaultFlex: 1,
    },
    {
      header: 'Product Name',
      name: 'title',
      userSelect: true,
      defaultFlex: 1,
    },
    {
      header: 'Quantity',
      name: 'quantity',
      userSelect: true,
      defaultFlex: 1,
    },
    {
      header: 'Picture',
      name: 'thumbnailUrl',
      userSelect: true,
      dataType: ColumnDataType.IMAGE,
    },
    {
      header: 'Public Notes',
      name: 'publicNote',
      userSelect: true,
      defaultFlex: 1,
    },
    {
      header: 'Private Notes',
      name: 'privateNote',
      userSelect: true,
      defaultFlex: 1,
    },
    {
      header: 'Received Quantity',
      name: 'humanReceiveQty',
      userSelect: true,
      defaultFlex: 1,
      render: (value: any) => {
        const { data: row } = value;
        const { humanReceiveQty, quantity } = row;
        return <Text>{`${humanReceiveQty}/${quantity}`}</Text>;
      },
    },
    {
      header: 'Waiting for Closing',
      name: 'waitCloseQty',
      userSelect: true,
      defaultFlex: 1,
    },
    {
      header: 'Closing',
      name: 'closingQty',
      userSelect: true,
      defaultFlex: 1,
    },
  ];

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      let poNumber = '';
      if(props.isModal && props.poInfo){
        poNumber = props.poInfo.poNumber
      } else {
        const parameters = getRouterParameters();
        poNumber = parameters['poNumber']
      }
    
      const newRecord = await fetchPo(poNumber);

      setRecord(newRecord);
      if (newRecord) {
        const newData = await fetchPoLineList(newRecord.poId);
        setData(newData);
        setValues(newRecord);
      } else {
        message.error('Fetch PO detail failed');
      }
      setLoading(false);
    };
    fetch();
  }, [props]);
  /**
   * delete Po
   */
  // const deletePo = useCallback(async () => {
  //   if (!record) {
  //     return;
  //   }
  //   try {
  //     setLoading(true);
  //     const res = await delPurchaseOrder(record.poId);
  //     setLoading(false);
  //     if (res) {
  //       message.success({ content: 'Deleted successfully' });
  //       history.push('/po/list');
  //     }
  //   } catch (error) {
  //     setLoading(false);
  //   }
  // }, [record, history]);

  /**
   * Abandon Po
   */
  const abandonPo = useCallback(async () => {
    if (!record) {
      return;
    }
    try {
      setLoading(true);
      const res = await abandonPurchaseOrder(record.poId);
      setLoading(false);
      if (res) {
        message.success({ content: 'Abandoned successfully' });
        history.push('/po/list');
      }
    } catch (error) {
      setLoading(false);
    }
  }, [record, history]);

  const onClickSkuCell = (data: ProductRow) => {
    const selObj = window.getSelection();
    let open = selObj && selObj.toString() ? false : true;

    if (open) {
      //console.log(data.productNum, data.productId, data);
      setCurrent(data);
      setDetailVisible(true);
    }
  };

  const onSelectionChange = (props: any) => {
    const { selected, data, unselected } = props;

    setSelected(selected);

    //console.log('sc', selected, data, unselected);
    if (selected === true && !unselected) {
      //console.log('ts->', selectedRows);
      if (Array.isArray(data)) {
        setSelectedRows(data as any);
      } else {
        setSelectedRows([...selectedRows, data]);
      }
    } else {
      if (!unselected) {
        setSelectedRows(Object.values(selected as any));
      } else {
        setSelectedRows((prev) => {
          //console.log('prev', prev);
          const exist = Array.isArray(prev) ? prev.filter((item) => item.fulfillmentPoLineNum === (data as any).fulfillmentPoLineNum) : [];

          //console.log('prev', prev, exist);
          if (exist.length > 0) {
            // return existing items
            return prev.filter((item) => item.fulfillmentPoLineNum !== (data as any).fulfillmentPoLineNum);
          } else {
            //console.log('d', data);
            if (data.fulfillmentPoLineNum) {
              if (Array.isArray(prev)) {
                prev.push(data);
              } else {
                //console.log('sr', selectedRows);
                if (selectedRows.length > 0) {
                  const exist2 = selectedRows.filter(item => item.fulfillmentPoLineNum === (prev as any).fulfillmentPoLineNum);

                  console.log('ex', exist2);
                  if (exist2.length > 0) {
                    return [selectedRows.filter(item => item.fulfillmentPoLineNum !== (prev as any).fulfillmentPoLineNum)];
                  } else {
                    return [...selectedRows, prev];
                  }
                } else {
                  return [prev];
                }
              }
            }
          }
          //console.log('p', prev, selectedRows);

          return [...prev];
        });
      }
    }
  };

  const openPrintLabelDialog = (useQuantity: boolean) => {
    const skus: any[] = [];
    const upcs: any[] = [];
    const ls: any[] = [];

    console.log('srs', selectedRows);
    selectedRows.forEach(e => {
      skus.push(e.sku);
      upcs.push(e.upc)
      ls.push({
        code: e.sku,
        title: e.title,
        quantity: useQuantity ? e.quantity : 1,
      });
    });
    //console.log('s', useQuantity, selectedRow, skus, upcs, ls);
    setSkuList(skus);
    setUpcList(upcs);
    setLabelData(ls);
    setPrintLabelVisible(true);
  };

  const printProductLabel = () => {
    let useQuantity = true;

    Modal.confirm({
      centered: true,
      content: <Row align="middle">
        <span>Apply source quantity to print task:&nbsp;</span>
        <Switch checkedChildren="Yes" unCheckedChildren="No" defaultChecked onChange={checked => useQuantity = checked} />
      </Row>,
      title: 'Confirm',
      onOk() {
        //console.log('use', useQuantity);
        openPrintLabelDialog(useQuantity);
      },
    });
  };

  /**
   * 
   */
  const updatePoControlStatus = useCallback(async (type: 'close' | 'open') => {
    if (!record) {
      return;
    }
    try {
      setLoading(true);
      const res = type === 'close' ? await poControlStatusClose(record.poId) : await poControlStatusOpen(record.poId);
      if (res) {
        message.success({ content: `${type === 'close' ? 'Close' : 'Reopen'} successfully` });
        const parameters = getRouterParameters();
        const newRecord = await fetchPo(parameters['poNumber']);
        setRecord(newRecord);
        if (newRecord) {
          setValues(newRecord);
        }
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
    }
  }, [record]);
  /**
   * handle save publicNote/privateNote
   */
  const handleSave = useCallback(async () => {
    if (!record) {
      return;
    }
    setSaveLoading(true);
    await patchPo(record.poId, values.publicNote, values.privateNote);
    //renew record&status
    const parameters = getRouterParameters();
    const newRecord = await fetchPo(parameters['poNumber']);
    setRecord(newRecord);
    if (newRecord) {
      setValues(newRecord);
    }
    setSaveLoading(false);
    setEdited(false);
    setChanged(false);
    message.success({ content: 'Update Purchase Order Successfully!' });
  }, [record, values]);

  useEffect(() => {
    setSnapshot({ ...record });
    form.setFieldsValue(values);
  }, [record, form, values]);

  return record ?(
    <>
      {!props.isModal && <HeaderTitle
        breadcrumb={[
          'PO',
          {
            type: 'link',
            content: 'Purchase Order Details',
            url: '/po/list',
          },
          `${record.poNumber}`,
        ]}
      />}
      <Spin spinning={loading}>
        <Card>
          <Descriptions size="small" column={3} labelStyle={{ display: 'inline-block', width: 200, textAlign: 'right' }}>
            <Descriptions.Item label={<FormLabel noColon>PO#</FormLabel>}>
              <CopySpan value={record.poNumber} />
            </Descriptions.Item>
            <Descriptions.Item label={<FormLabel noColon>Warehouse</FormLabel>}>
              <CopySpan value={record.warehouseCode} />
            </Descriptions.Item>
            <Descriptions.Item label={<FormLabel noColon>Container #</FormLabel>}>
              <CopySpan value={record.container} />
            </Descriptions.Item>
            <Descriptions.Item label={<FormLabel noColon>PO Status</FormLabel>}>
              <CopySpan value={poStatusMap[record?.poStatusNum]} />
            </Descriptions.Item>
            <Descriptions.Item label={<FormLabel noColon>Supplier</FormLabel>}>
              <CopySpan value={record.vendorName} />
            </Descriptions.Item>
            <Descriptions.Item label={<FormLabel noColon>Created By</FormLabel>}>
              <CopySpan value={record.createBy} />
            </Descriptions.Item>
            <Descriptions.Item label={<FormLabel noColon>Order Date</FormLabel>}>
              <CopySpan value={record.poDate ? convertTimeByUtc(dayjs(record.poDate).format(DEFAULT_DATE_TIME_FORMAT)) : ''} />
            </Descriptions.Item>
            <Descriptions.Item label={<FormLabel noColon>Date Created</FormLabel>}>
              <CopySpan value={record.createDate ? convertTimeByUtc(dayjs(record.createDate).format(DEFAULT_DATE_TIME_FORMAT)) : ''} />
            </Descriptions.Item>
            <Descriptions.Item label={<FormLabel noColon>Terms</FormLabel>}>
              <CopySpan value={record.terms} />
            </Descriptions.Item>
            <Descriptions.Item label={<FormLabel noColon>Date Last Updated</FormLabel>}>
              <CopySpan value={record.updateDate ? convertTimeByUtc(dayjs(record.updateDate).format(DEFAULT_DATE_TIME_FORMAT)) : ''} />
            </Descriptions.Item>
            <Descriptions.Item label={<FormLabel noColon>Order Cancel Date</FormLabel>}>
              <CopySpan value={record.cancelAfterDate ? convertTimeByUtc(dayjs(record.cancelAfterDate).format(DEFAULT_DATE_TIME_FORMAT)) : ''} />
            </Descriptions.Item>
            <Descriptions.Item label={<FormLabel noColon>Control Status</FormLabel>}>
              <CopySpan value={poControlStatus[`${record.controlStatus}`] || ''} />
            </Descriptions.Item>
            <Descriptions.Item label={<FormLabel noColon>Doc ID</FormLabel>}>
              <CopySpan value={record.poUuid || ''} />
            </Descriptions.Item>
          </Descriptions>
        </Card>
        <Row justify="center" style={{ marginTop: 20 }}>
          <Space>
            <ExportCSV
              columns={columns}
              data={data}
              scope="Purchase Order Line Items"
              title="Export Purchase Order Line Items"
            />
            <Dropdown
              overlay={
                <Menu>
                  {[0, 1].includes(record.controlStatus) && !record.receivedQuantity && (
                    <Menu.Item key="6">
                      <Button
                        id={`po_detail_more_abandon`}
                        type="text"
                        style={{ width: '100%', textAlign: 'left' }}
                        className="hover-danger-button-2"
                        icon={<DeleteOutlined />}
                        onClick={() => {
                          Modal.confirm({
                            title: 'Are you sure you want Abandon this PO?',
                            icon: <ExclamationCircleOutlined />,
                            content: `PO Number： ${record?.poNumber}`,
                            okText: 'Yes',
                            okType: 'default',
                            okButtonProps: { className: 'hover-danger-button' },
                            cancelText: 'No',
                            autoFocusButton: 'cancel',
                            onOk() {
                              abandonPo();
                            },
                            onCancel() { },
                          });
                        }}
                      >
                        Abandon
                      </Button>
                    </Menu.Item>
                  )}
                  {/* <Menu.Item key="4">
                    <Button
                      id={`po_detail_more_delete`}
                      type="text"
                      style={{ width: '100%', textAlign: 'left' }}
                      className="hover-danger-button-2"
                      icon={<DeleteOutlined />}
                      onClick={() => {
                        Modal.confirm({
                          title: 'Are you sure you want delete this PO?',
                          icon: <ExclamationCircleOutlined />,
                          content: `PO Number： ${record?.poNumber}`,
                          okText: 'Yes',
                          okType: 'default',
                          okButtonProps: { className: 'hover-danger-button' },
                          cancelText: 'No',
                          autoFocusButton: 'cancel',
                          onOk() {
                            deletePo();
                          },
                          onCancel() { },
                        });
                      }}
                    >
                      Delete
                    </Button>
                  </Menu.Item> */}
                  <Menu.Item key="5">
                    {
                      record.controlStatus === 2 ?
                        <Button
                          id={`po_detail_more_open`}
                          type="text"
                          style={{ width: '100%', textAlign: 'left' }}
                          icon={<RedoOutlined />}
                          onClick={() => {
                            updatePoControlStatus('open');
                          }}
                        >
                          Reopen
                        </Button> :
                        <Button
                          id={`po_detail_more_close`}
                          type="text"
                          style={{ width: '100%', textAlign: 'left' }}
                          className="hover-danger-button-2"
                          icon={<CloseOutlined />}
                          onClick={() => {
                            Modal.confirm({
                              title: 'Are you sure you want to close this PO?',
                              icon: <ExclamationCircleOutlined />,
                              content: `PO Number： ${record?.poNumber}`,
                              okText: 'Yes',
                              okType: 'default',
                              okButtonProps: { className: 'hover-danger-button' },
                              cancelText: 'No',
                              autoFocusButton: 'cancel',
                              onOk() {
                                updatePoControlStatus('close');
                              },
                              onCancel() { },
                            });
                          }}
                        >
                          Close
                        </Button>
                    }
                  </Menu.Item>
                </Menu>
              }
              trigger={['click']}
            >
              <Button id={`po_detail_more_button`}>
                <CaretDownOutlined />
                More
              </Button>
            </Dropdown>
          </Space>
        </Row>
        <GreyCardWrapper style={{ marginTop: 20 }}>
          <Tabs defaultActiveKey="items" type="card">
            <Tabs.TabPane tab="Line Items" key="items">
              <GreyCardWrapper>
                <Row justify='start'>
                  <Space>
                    <Button onClick={()=>{
                      const skulist = Array.from(new Set(data.map(i=>i.sku))).toString()
                      history.push(`/catalog/view?skulist=${skulist}`)
                    }}>Show items in Catalog</Button>
                    <Button
                      disabled={selectedRows.length === 0}
                      onClick={printProductLabel}
                    >
                      Print Product Label
                    </Button>
                  </Space>
                </Row>
                <DataGridPlus
                  checkboxColumn
                  columns={columns}
                  dataSource={data}
                  idProperty="poLineNum"
                  loading={loading}
                  onSelectionChange={onSelectionChange}
                  pagination={false}
                  selected={selected}
                  /* eslint-disable react/jsx-props-no-spreading */
                  {...({} as any)}
                />
              </GreyCardWrapper>
            </Tabs.TabPane>
            <Tabs.TabPane tab="Details" key="details">
              <GreyCardWrapper>
                <FormPlus
                  className="compact-form"
                  /* eslint-disable react/jsx-props-no-spreading */
                  {...detailLayout}
                  snapshot={snapshot}
                  form={form}
                  onChanged={(changed: boolean) => {
                    setChanged(changed);
                    setValues(form.getFieldsValue());
                  }}
                >
                  <Row>
                    <Col span={12}>
                      <Card>
                        <Form.Item
                          label={<FormLabel>Shipping Carrier</FormLabel>}
                        >
                          <Text />
                        </Form.Item>
                        <Form.Item
                          label={<FormLabel>Shipping Class</FormLabel>}
                        >
                          <Text />
                        </Form.Item>
                        <Form.Item
                          label={<FormLabel>Ship To Warehouse</FormLabel>}
                        >
                          <Text>{record.warehouseCode}</Text>
                        </Form.Item>
                        <Form.Item
                          label={<FormLabel>Ship To Address</FormLabel>}
                        >
                          <Text />
                        </Form.Item>
                        <Form.Item
                          label={<FormLabel>Requested Ship Date</FormLabel>}
                        >
                          <Text>
                            {record.requestShipDate
                              ? dayjs(record.requestShipDate).format(
                                'MM/DD/YYYY'
                              )
                              : ''}
                          </Text>
                        </Form.Item>
                        <Form.Item
                          label={<FormLabel>Actual Ship Date</FormLabel>}
                        >
                          <Text />
                        </Form.Item>
                        <Form.Item
                          label={<FormLabel>Arrival Due Date</FormLabel>}
                        >
                          <Text>
                            {record.arrvalDueDate
                              ? dayjs(record.arrvalDueDate).format('MM/DD/YYYY')
                              : ''}
                          </Text>
                        </Form.Item>
                      </Card>
                    </Col>
                    <Col span={12}>
                      <Card>
                        <Row justify="end" style={{ marginBottom: 16 }}>
                          <FormSubmitButton
                            loading={saveLoading}
                            changed={changed}
                            edited={edited}
                            onEdit={() => {
                              setEdited(true);
                            }}
                            style={{ width: 180 }}
                            onSubmit={handleSave}
                          />
                        </Row>
                        <Form.Item
                          name="publicNote"
                          label={<FormLabel>Public Notes</FormLabel>}
                        >
                          <TextArea autoFocus rows={4} edited={edited} />
                        </Form.Item>
                        <Form.Item
                          name="privateNote"
                          label={<FormLabel>Private Notes</FormLabel>}
                        >
                          <TextArea rows={4} edited={edited} />
                        </Form.Item>
                      </Card>
                    </Col>
                  </Row>
                </FormPlus>
              </GreyCardWrapper>
            </Tabs.TabPane>
          </Tabs>
        </GreyCardWrapper>
      </Spin>
      {
        detailVisible && (
          <ProductDetail
            visible={detailVisible}
            onHide={() => setDetailVisible(false)}
            productId={current.productId}
          />
        )
      }
      {printLabelVisible && (
        <PrintLabelDialog
          visible={true}
          onHide={() => setPrintLabelVisible(false)}
          printType="product"
          labelData={labelData}
          upcList={upcList}
          skuList={skuList}
        />
      )}
    </>
  ): null;
};

export default PoDetail;
